import { useCallback, useState } from 'react';
import {
  Alert, Button, Card, Col, DatePicker, Form, message, Row,
  Statistic, Tabs, Typography
} from 'antd';
import { ErpMasterShell } from '../../../components/common/erp-grid';
import { accountsApi } from './accountsApi';
import './accounts.css';

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

// Stage 6B.1 — GST Reports page (foundation).
// All five endpoints return the same envelope:
//   { rows: [], summary: {...}, meta: { period, basis, disclaimer } }
// The page exposes a date-range picker + five tabs, each calling the
// matching endpoint and rendering the summary block as Statistic
// cards. Detail-row tables are deliberately minimal — this is a
// review surface, not a filing tool.
function paramsFrom(values) {
  const out = {};
  if (Array.isArray(values?.range) && values.range[0] && values.range[1]) {
    out.fromDate = values.range[0].format('YYYY-MM-DD');
    out.toDate   = values.range[1].format('YYYY-MM-DD');
  }
  return out;
}

function StatCard({ label, value, suffix }) {
  return (
    <Col xs={12} sm={8} md={6} lg={4}>
      <Card size="small">
        <Statistic title={label} value={value || 0} precision={2} suffix={suffix} />
      </Card>
    </Col>
  );
}

function Disclaimer({ data }) {
  if (!data || !data.meta) return null;
  return (
    <Alert
      type="warning" showIcon banner
      style={{ marginTop: 8 }}
      message="Draft foundation"
      description={data.meta.disclaimer}
    />
  );
}

function Gstr1Tab({ params }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const refresh = useCallback(async () => {
    setLoading(true);
    try { setData(await accountsApi.gstr1Summary(params)); }
    catch (e) { message.error(e?.response?.data?.error?.message || 'Failed to load GSTR-1 summary.'); }
    finally { setLoading(false); }
  }, [params]);
  return (
    <>
      <Button type="primary" onClick={refresh} loading={loading} style={{ marginBottom: 12 }}>
        Refresh
      </Button>
      {data ? (
        <>
          <Row gutter={[8, 8]}>
            <StatCard label="Invoices" value={data.summary?.invoiceCount} />
            <StatCard label="Taxable" value={data.summary?.taxableValue} suffix="₹" />
            <StatCard label="CGST" value={data.summary?.cgstAmount} suffix="₹" />
            <StatCard label="SGST" value={data.summary?.sgstAmount} suffix="₹" />
            <StatCard label="IGST" value={data.summary?.igstAmount} suffix="₹" />
            <StatCard label="Total" value={data.summary?.invoiceTotal} suffix="₹" />
          </Row>
          <Paragraph type="secondary" style={{ marginTop: 12 }}>
            {data.rows?.length || 0} bucketed rows by GST invoice type / supply type / place of supply.
          </Paragraph>
          <Disclaimer data={data} />
        </>
      ) : (
        <Paragraph type="secondary">Click <Text strong>Refresh</Text> to fetch the GSTR-1 outward-supply summary.</Paragraph>
      )}
    </>
  );
}

function Gstr3bTab({ params }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const refresh = useCallback(async () => {
    setLoading(true);
    try { setData(await accountsApi.gstr3bSummary(params)); }
    catch (e) { message.error(e?.response?.data?.error?.message || 'Failed to load GSTR-3B summary.'); }
    finally { setLoading(false); }
  }, [params]);
  return (
    <>
      <Button type="primary" onClick={refresh} loading={loading} style={{ marginBottom: 12 }}>
        Refresh
      </Button>
      {data ? (
        <>
          <Title level={5} style={{ marginTop: 0 }}>Outward (sales)</Title>
          <Row gutter={[8, 8]}>
            <StatCard label="Taxable" value={data.summary?.outwardTaxable} suffix="₹" />
            <StatCard label="CGST" value={data.summary?.outwardCgst} suffix="₹" />
            <StatCard label="SGST" value={data.summary?.outwardSgst} suffix="₹" />
            <StatCard label="IGST" value={data.summary?.outwardIgst} suffix="₹" />
            <StatCard label="Cess" value={data.summary?.outwardCess} suffix="₹" />
          </Row>
          <Title level={5}>Inward / ITC eligible (purchases)</Title>
          <Row gutter={[8, 8]}>
            <StatCard label="Taxable" value={data.summary?.itcTaxable} suffix="₹" />
            <StatCard label="CGST" value={data.summary?.itcCgst} suffix="₹" />
            <StatCard label="SGST" value={data.summary?.itcSgst} suffix="₹" />
            <StatCard label="IGST" value={data.summary?.itcIgst} suffix="₹" />
            <StatCard label="Cess" value={data.summary?.itcCess} suffix="₹" />
          </Row>
          <Title level={5}>Net</Title>
          <Row gutter={[8, 8]}>
            <StatCard label="Net GST Payable" value={data.summary?.netGstPayable} suffix="₹" />
          </Row>
          <Disclaimer data={data} />
        </>
      ) : (
        <Paragraph type="secondary">Click <Text strong>Refresh</Text> to fetch the GSTR-3B summary.</Paragraph>
      )}
    </>
  );
}

function HsnTab({ params }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const refresh = useCallback(async () => {
    setLoading(true);
    try { setData(await accountsApi.hsnSummary(params)); }
    catch (e) { message.error(e?.response?.data?.error?.message || 'Failed to load HSN summary.'); }
    finally { setLoading(false); }
  }, [params]);
  return (
    <>
      <Button type="primary" onClick={refresh} loading={loading} style={{ marginBottom: 12 }}>
        Refresh
      </Button>
      {data ? (
        <>
          <Row gutter={[8, 8]}>
            <StatCard label="Invoices" value={data.summary?.invoiceCount} />
            <StatCard label="Quantity" value={data.summary?.totalQuantity} />
            <StatCard label="Taxable" value={data.summary?.taxableValue} suffix="₹" />
            <StatCard label="CGST" value={data.summary?.cgstAmount} suffix="₹" />
            <StatCard label="SGST" value={data.summary?.sgstAmount} suffix="₹" />
            <StatCard label="IGST" value={data.summary?.igstAmount} suffix="₹" />
            <StatCard label="Total" value={data.summary?.lineTotal} suffix="₹" />
          </Row>
          <Paragraph type="secondary" style={{ marginTop: 12 }}>
            {data.rows?.length || 0} HSN/SAC × period rows.
          </Paragraph>
          <Disclaimer data={data} />
        </>
      ) : (
        <Paragraph type="secondary">Click <Text strong>Refresh</Text> to fetch the HSN/SAC summary.</Paragraph>
      )}
    </>
  );
}

function ItcTab({ params }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const refresh = useCallback(async () => {
    setLoading(true);
    try { setData(await accountsApi.itcSummary(params)); }
    catch (e) { message.error(e?.response?.data?.error?.message || 'Failed to load ITC summary.'); }
    finally { setLoading(false); }
  }, [params]);
  return (
    <>
      <Button type="primary" onClick={refresh} loading={loading} style={{ marginBottom: 12 }}>
        Refresh
      </Button>
      {data ? (
        <>
          <Row gutter={[8, 8]}>
            <StatCard label="Bills" value={data.summary?.billCount} />
            <StatCard label="Taxable" value={data.summary?.taxableValue} suffix="₹" />
            <StatCard label="CGST" value={data.summary?.cgstAmount} suffix="₹" />
            <StatCard label="SGST" value={data.summary?.sgstAmount} suffix="₹" />
            <StatCard label="IGST" value={data.summary?.igstAmount} suffix="₹" />
            <StatCard label="Total" value={data.summary?.billTotal} suffix="₹" />
          </Row>
          <Paragraph type="secondary" style={{ marginTop: 12 }}>
            {data.rows?.length || 0} period × supply-type rows.
          </Paragraph>
          <Disclaimer data={data} />
        </>
      ) : (
        <Paragraph type="secondary">Click <Text strong>Refresh</Text> to fetch the ITC summary.</Paragraph>
      )}
    </>
  );
}

function GstPayableTab({ params }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const refresh = useCallback(async () => {
    setLoading(true);
    try { setData(await accountsApi.gstPayable(params)); }
    catch (e) { message.error(e?.response?.data?.error?.message || 'Failed to load GST payable.'); }
    finally { setLoading(false); }
  }, [params]);
  return (
    <>
      <Button type="primary" onClick={refresh} loading={loading} style={{ marginBottom: 12 }}>
        Refresh
      </Button>
      {data ? (
        <>
          <Row gutter={[8, 8]}>
            <StatCard label="Net Payable" value={data.summary?.netGstPayable} suffix="₹" />
            <StatCard label="Outward Tax" value={data.summary?.outwardTotalTax} suffix="₹" />
            <StatCard label="ITC Total" value={data.summary?.itcTotalTax} suffix="₹" />
          </Row>
          <Disclaimer data={data} />
        </>
      ) : (
        <Paragraph type="secondary">Click <Text strong>Refresh</Text> for the net GST payable figure.</Paragraph>
      )}
    </>
  );
}

export default function GstReportsPage() {
  const [form] = Form.useForm();
  const [params, setParams] = useState({});

  const onSubmit = useCallback((values) => {
    setParams(paramsFrom(values));
  }, []);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
        <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>GST Reports</Title>
        <Alert type="warning" banner showIcon style={{ marginBottom: 10 }}
          message="For review only — not filed to the GST portal."
          description="Numbers compute live from posted tenant invoices + bills. Live IRP / e-way / GSTR submission belongs to a later stage." />
        <Card size="small" className="erp-accounts-filter" style={{ marginBottom: 10 }}>
          <Form layout="inline" form={form} onFinish={onSubmit} size="small">
            <Form.Item name="range" label="Period">
              <RangePicker size="small" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" size="small" htmlType="submit">Apply</Button>
            </Form.Item>
          </Form>
        </Card>
        <Tabs
          size="small"
          items={[
            { key: 'gstr1', label: 'GSTR-1', children: <Gstr1Tab params={params} /> },
            { key: 'gstr3b', label: 'GSTR-3B', children: <Gstr3bTab params={params} /> },
            { key: 'hsn', label: 'HSN Summary', children: <HsnTab params={params} /> },
            { key: 'itc', label: 'ITC Summary', children: <ItcTab params={params} /> },
            { key: 'payable', label: 'GST Payable', children: <GstPayableTab params={params} /> }
          ]}
        />
      </div>
    </ErpMasterShell>
  );
}
